/**
 * Bidirectional mapping between marketing display names and internal option
 * root tickers used by the data pipeline.
 *
 * Internal  →  Display   (getOptionsDisplayTicker)
 *   SPXW    →  SPX       (weekly — what traders mean by "SPX")
 *   SPX     →  SPXAM     (monthly AM-settled)
 *
 * Display   →  Internal  (normalizeOptionsTicker)
 *   SPX     →  SPXW
 *   SPXAM   →  SPX
 *
 * Price data always uses the raw index ticker and must NOT pass through
 * either function. Apply only in options contexts.
 */
const INTERNAL_TO_DISPLAY: Record<string, string> = {
  SPXW: 'SPX',
  SPX: 'SPXAM',
};

const DISPLAY_TO_INTERNAL: Record<string, string> = {
  SPX: 'SPXW',
  SPXAM: 'SPX',
};

/**
 * Returns the marketing display name for an internal options root ticker.
 * Use when rendering a ticker label. Unmapped tickers are returned unchanged.
 */
export function getOptionsDisplayTicker(symbol: string): string {
  return INTERNAL_TO_DISPLAY[symbol.toUpperCase()] ?? symbol;
}

/**
 * Normalizes a user-entered or linked display ticker to the internal options
 * root expected by the API and WebSocket channels.
 *
 * Accepts display names (SPX → SPXW, SPXAM → SPX) as well as internal
 * tickers directly (SPXW, SPY, etc. pass through unchanged), so it is safe
 * to call on any input source.
 */
export function normalizeOptionsTicker(symbol: string): string {
  return DISPLAY_TO_INTERNAL[symbol.toUpperCase()] ?? symbol;
}

/**
 * Every spelling of every cash index the platform knows about, mapped to the
 * underlying index symbol its price data lives under.
 *
 * The keys are what a reader might type or a feed might send: the bare root,
 * the options roots (weekly and AM-settled), and — for SPX — the display alias
 * `SPXAM`. The value is the price/OHLC symbol, which is always the plain index.
 *
 * ⚠️ **Matched exactly, never by prefix.** Every family here has ordinary
 * listed tickers that share its opening letters: `SPXL`/`SPXS`/`SPXU` are
 * leveraged ETFs, `VIXY`/`VXX` are volatility ETPs, `RUTH` is a restaurant
 * group. A `startsWith` test would take all of them down while looking correct.
 *
 * ⚠️ **`NDXP`, not `NDXW`.** The Nasdaq weekly root breaks the usual
 * base-plus-W pattern. Confirmed against QuantForge's live trade data
 * 2026-08-06, along with `RUTW` and `VIXW`.
 */
const INDEX_PRICE_SYMBOL: Readonly<Record<string, string>> = {
  SPX: 'SPX',
  SPXW: 'SPX',
  SPXAM: 'SPX',
  NDX: 'NDX',
  NDXP: 'NDX',
  RUT: 'RUT',
  RUTW: 'RUT',
  VIX: 'VIX',
  VIXW: 'VIX',
};

/** Strips the `^`/`.`/`$` prefixes different feeds put in front of an index. */
function bareSymbol(symbol: string): string {
  return symbol.trim().toUpperCase().replace(/^[$^.]/, '');
}

/**
 * Resolves a ticker to the underlying INDEX symbol used for price/OHLC data.
 *
 * Per the module contract, price data must use the raw index ticker — never the
 * options root. Every member of an index family shares one underlying (SPX,
 * SPXW and SPXAM all settle against the SPX cash index); everything else is its
 * own underlying and passes through unchanged. Use this for candle/OHLC fetches
 * and `ohlc:{symbol}` channels, NOT `normalizeOptionsTicker` (which would key
 * price data off SPXW and return no candles).
 */
export function underlyingPriceTicker(symbol: string): string {
  return INDEX_PRICE_SYMBOL[bareSymbol(symbol)] ?? symbol;
}

/**
 * Whether a symbol is a cash index rather than a listed company.
 *
 * ⚠️ **This grants access to a narrower page, it does not refuse the symbol.**
 * It used to do the latter — under the name `isIndexOnlySymbol` it rejected SPX
 * from the Ticker Explorer outright, because a cash index has no financials, no
 * filings, no shares outstanding and no analyst coverage, so most of that page
 * rendered empty and read as broken.
 *
 * That guard predated the Gamma tab, which is the one section that works
 * *better* for an index than for most equities — index gamma is the thing
 * people most want to look at. Blocking the page to protect the tabs that have
 * nothing to show was throwing out the tab that does.
 *
 * So indices now open, and `TickerDetailPage` shows them Overview and Gamma
 * only. See `NAV_FOR_INDEX` there for which sections survive and why.
 */
export function isIndexSymbol(symbol: string): boolean {
  return bareSymbol(symbol) in INDEX_PRICE_SYMBOL;
}
